// Activation Flow Layer — improves first-session workspace setup and trial conversion
//
// This module renders two sequential post-onboarding modals:
//   1. Quick Settings Wizard  — collects business_name, currency, timezone
//   2. Activation Path Modal  — lets the user choose their starting workflow
//
// Both modals show exactly once per user (persisted via backend user profile).
// The settings wizard is always shown before the path selection modal.
//
// Gate rules (evaluated once on first user load):
//   • Admins are never shown either modal.
//   • Wizard is shown whenever settings_initialized !== true, regardless of
//     whether individual fields already have values.  settings_initialized is
//     the sole authoritative flag for intentional wizard completion.
//   • Path modal is shown when settings_initialized === true but activation_path
//     is not yet set.
//   • Once both flags are set in the backend the flow never appears again.

import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CURRENCIES, TIMEZONES } from "../lib/utils";
import { toast } from "sonner";
import {
  Settings,
  FileText,
  PiggyBank,
  TrendingUp,
  Clock,
  LayoutDashboard,
  ArrowRight,
} from "lucide-react";

// ── Activation Path options ──────────────────────────────────────────────────
const ACTIVATION_PATHS = [
  {
    value: "invoice",
    icon: FileText,
    label: "Send my first invoice",
    description: "Create and send professional invoices to clients",
  },
  {
    value: "budget",
    icon: PiggyBank,
    label: "Set up my budget",
    description: "Plan income, expenses, and monthly budgets",
  },
  {
    value: "savings",
    icon: TrendingUp,
    label: "Track my savings",
    description: "Monitor savings goals and financial runway",
  },
  {
    value: "time",
    icon: Clock,
    label: "Track my time",
    description: "Log hours and bill clients accurately",
  },
  {
    value: "explore",
    icon: LayoutDashboard,
    label: "Explore the dashboard",
    description: "Get familiar with all workspace features first",
  },
];

// ── ActivationFlow ────────────────────────────────────────────────────────────
// Renders the settings wizard and/or path modal depending on what the user
// has already completed.  Call this component inside DashboardPage so it
// appears immediately after the user enters the dashboard for the first time.
export default function ActivationFlow() {
  const { user, isAdmin, updateProfile } = useAuth();

  // Which modal is currently visible
  const [showSettingsWizard, setShowSettingsWizard] = useState(false);
  const [showPathModal, setShowPathModal] = useState(false);

  // Settings wizard form state
  const [wizardData, setWizardData] = useState({
    business_name: "",
    default_currency: "PHP",
    default_timezone: "UTC",
  });
  const [wizardSaving, setWizardSaving] = useState(false);

  // Path selection state
  const [pathSaving, setPathSaving] = useState(false);

  // ── One-time gate evaluation ───────────────────────────────────────────────
  // We evaluate which (if any) modal to show exactly once — when the user
  // object first becomes available.  After that, state transitions are driven
  // exclusively by the handler callbacks below, not by re-running this effect.
  // This prevents any modal from being re-opened by a user state update that
  // occurs after a save (which would create a modal loop).
  const evaluatedRef = useRef(false);

  useEffect(() => {
    // Wait until the user object is available and skip if already evaluated
    if (!user || evaluatedRef.current) return;
    // Admins never go through the activation flow
    if (isAdmin) {
      evaluatedRef.current = true;
      return;
    }

    evaluatedRef.current = true;

    // settings_initialized is the sole gate for the wizard.
    // It represents intentional completion, not inferred field-level completion.
    // Show the wizard whenever it has not been explicitly set to true,
    // even if some fields already contain values (prefill them for convenience).
    if (!user.settings_initialized) {
      setWizardData({
        business_name: user.business_name || "",
        default_currency: user.default_currency || "PHP",
        default_timezone: user.default_timezone || "UTC",
      });
      setShowSettingsWizard(true);
      return;
    }

    // Settings are confirmed complete; show path modal if no path chosen yet
    if (!user.activation_path) {
      setShowPathModal(true);
    }
    // Both flags set → nothing to show; dashboard renders normally
  }, [user, isAdmin]);

  // ── Settings Wizard submit ─────────────────────────────────────────────────
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    if (!wizardData.business_name.trim()) {
      toast.error("Please enter your business name.");
      return;
    }
    setWizardSaving(true);
    try {
      // Save the three required fields plus the intentional-completion flag
      await updateProfile({
        ...wizardData,
        settings_initialized: true,
      });
      toast.success("Workspace settings saved!");
      // Close wizard; advance to path modal only if no path was already chosen
      setShowSettingsWizard(false);
      if (!user?.activation_path) {
        setShowPathModal(true);
      }
      // Note: the evaluated gate (evaluatedRef) remains true so the useEffect
      // will NOT re-open any modal when the user state updates after this save.
    } catch {
      toast.error("Failed to save settings. Please try again.");
    } finally {
      setWizardSaving(false);
    }
  };

  // ── Activation Path selection ──────────────────────────────────────────────
  const handlePathSelect = async (pathValue) => {
    setPathSaving(true);
    try {
      await updateProfile({ activation_path: pathValue });
      toast.success("Great! Your workspace is personalized.");
      // Close path modal; both flags are now set — dashboard renders normally
      setShowPathModal(false);
      // Note: evaluatedRef is already true; the user state update from this
      // save will not re-trigger the gate effect, so no second modal appears.
    } catch {
      toast.error("Failed to save your selection. Please try again.");
    } finally {
      setPathSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── STEP 1: Quick Settings Wizard ─────────────────────────────────── */}
      {/* Activation Flow Layer — Required Quick Settings Wizard */}
      <Dialog open={showSettingsWizard}>
        <DialogContent
          className="sm:max-w-md"
          // Prevent closing by clicking outside or pressing Escape
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Settings className="h-5 w-5 text-blue-500" />
              <DialogTitle>Set up your workspace</DialogTitle>
            </div>
            <DialogDescription>
              Quick settings to personalize your experience. You can update these
              anytime in Account Settings.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSettingsSubmit} className="space-y-4 mt-2">
            {/* Business Name */}
            <div className="space-y-1.5">
              <Label htmlFor="af-business-name">Business Name</Label>
              <Input
                id="af-business-name"
                placeholder="e.g. Jane's Design Studio"
                value={wizardData.business_name}
                onChange={(e) =>
                  setWizardData((d) => ({ ...d, business_name: e.target.value }))
                }
                required
              />
            </div>

            {/* Default Currency */}
            <div className="space-y-1.5">
              <Label htmlFor="af-currency">Default Currency</Label>
              <Select
                value={wizardData.default_currency}
                onValueChange={(v) =>
                  setWizardData((d) => ({ ...d, default_currency: v }))
                }
              >
                <SelectTrigger id="af-currency">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Timezone */}
            <div className="space-y-1.5">
              <Label htmlFor="af-timezone">Timezone</Label>
              <Select
                value={wizardData.default_timezone}
                onValueChange={(v) =>
                  setWizardData((d) => ({ ...d, default_timezone: v }))
                }
              >
                <SelectTrigger id="af-timezone">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" disabled={wizardSaving}>
              {wizardSaving ? "Saving…" : "Save & Continue"}
              {!wizardSaving && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── STEP 2: Activation Path Modal ─────────────────────────────────── */}
      {/* Activation Flow Layer — Activation Path Selection Modal */}
      {/* Only reachable after settings_initialized === true */}
      <Dialog open={showPathModal}>
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>What would you like to set up first?</DialogTitle>
            <DialogDescription>
              Choose your starting path. You can explore everything else any time.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-2">
            {ACTIVATION_PATHS.map(({ value, icon: Icon, label, description }) => (
              <button
                key={value}
                onClick={() => !pathSaving && handlePathSelect(value)}
                disabled={pathSaving}
                className="w-full text-left p-3 rounded-lg border border-border hover:border-blue-500/50 hover:bg-muted transition-all group disabled:opacity-60"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {label}
                    </p>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-blue-500 transition-colors flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
